import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api/client';
import { getAuth, saveAuth } from '../utils/auth';

interface DemoStatus {
  is_demo: boolean;
  demo_expires_at: string;
  days_remaining: number;
  demo_owner_email: string;
}

interface Persona {
  key: string;
  name: string;
  role: string;
  credential_level: string;
  user_id: string;
}

/** Where "Upgrade / request access" goes: the beta request form (#1153). */
export const UPGRADE_URL = 'https://responsekit.net/welcome?from=demo#signup';

/** "Trial: 11 days left" / "1 day left" / "last day". */
export function trialLabel(daysRemaining: number): string {
  if (daysRemaining <= 0) return 'last day';
  return `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`;
}

/** Record a CTA click for marketing; never blocks or breaks the link. */
function logCtaClick(cta: string): void {
  apiFetch('/api/demo/cta', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cta }),
    skipLogoutOn401: true,
  }).catch(() => {});
}

/**
 * Persistent banner shown at the top of the app for demo tenants.
 * Shows expiry countdown and a persona switcher dropdown.
 */
export default function DemoBanner() {
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [activePersona, setActivePersona] = useState<string>('owner');
  const [switching, setSwitching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  // The banner is fixed and can wrap to two rows on a phone, so publish its
  // real height for App's top padding instead of assuming one 40px row.
  // No deps: the banner mounts only once the demo status has loaded.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const publish = () => {
      document.documentElement.style.setProperty('--demo-banner-h', `${el.offsetHeight}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  });

  useEffect(() => {
    const auth = getAuth();
    if (!auth) return;
    setCurrentUserEmail(auth.user.email ?? null);

    apiFetch<DemoStatus | null>('/api/demo/status', { skipLogoutOn401: true })
      .then(data => { if (data?.is_demo) setStatus(data); })
      .catch(() => {});

    apiFetch<Persona[] | null>('/api/demo/personas', { skipLogoutOn401: true })
      .then(data => { if (Array.isArray(data)) setPersonas(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!status) return null;

  async function switchPersona(key: string) {
    setSwitching(true);
    setDropdownOpen(false);
    try {
      const auth = getAuth();
      if (!auth) return;

      // Store original owner token if not already stored
      if (!localStorage.getItem('rk_demo_owner_token')) {
        localStorage.setItem('rk_demo_owner_token', auth.token);
      }

      let token: string;
      if (key === 'owner') {
        token = localStorage.getItem('rk_demo_owner_token') || auth.token;
      } else {
        // eslint-disable-next-line no-restricted-syntax -- Intentionally uses
        // the owner token (not the currently-active persona token) to call
        // switch-persona. apiFetch would send whatever the current token is,
        // which defeats the swap.
        const res = await fetch('/api/demo/switch-persona', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('rk_demo_owner_token') || auth.token}`,
          },
          body: JSON.stringify({ persona_key: key }),
        });
        if (!res.ok) throw new Error('Switch failed');
        const data = await res.json();
        token = data.access_token;
      }

      // Decode JWT payload to get user info
      const payload = JSON.parse(atob(token.split('.')[1]));
      saveAuth(token, {
        id: payload.sub,
        email: payload.email,
        name: payload.email,
        mfa_verified: true,
        credential_level: payload.credential_level || '',
        role: payload.role || 'crew',
        tenant_slug: payload.tenant_slug,
      });
      setActivePersona(key);
      window.location.reload();
    } catch {
      // Silently fail — user stays on current persona
    } finally {
      setSwitching(false);
    }
  }

  const isOwner = currentUserEmail !== null && status !== null
    ? currentUserEmail === status.demo_owner_email
    : true;

  function openAdminPanel() {
    window.open('https://demo.responsekit.net/admin', '_blank', 'noopener,noreferrer');
  }

  const currentLabel = activePersona === 'owner'
    ? 'You (Admin)'
    : personas.find(p => p.key === activePersona)?.name || activePersona;

  return (
    <div ref={bannerRef} data-testid="demo-banner" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      borderBottom: '2px solid var(--accent)',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      fontSize: 13,
      color: '#ccc',
      gap: '4px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px' }}>
        <span style={{
          background: 'var(--accent)',
          color: '#fff',
          padding: '2px 8px',
          borderRadius: 4,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
          Demo
        </span>
        <span data-testid="demo-trial-countdown">
          Trial: <strong style={{ color: status.days_remaining <= 2 ? '#e74c3c' : '#fff' }}>
            {trialLabel(status.days_remaining)}
          </strong>
        </span>
        <a
          href={UPGRADE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logCtaClick('upgrade')}
          data-testid="demo-upgrade-link"
          aria-label="Upgrade / request access (opens in a new tab)"
          style={{
            color: '#9ecbff', fontSize: 13, textDecoration: 'underline',
            display: 'inline-flex', alignItems: 'center', minHeight: 24, padding: '2px 4px',
            whiteSpace: 'nowrap',
          }}
        >
          Upgrade / request access
        </a>
      </div>

      {isOwner ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={openAdminPanel}
            style={{
              background: 'rgba(46,204,113,0.15)',
              border: '1px solid rgba(46,204,113,0.3)',
              borderRadius: 6,
              padding: '4px 12px',
              color: '#2ecc71',
              fontSize: 13,
              cursor: 'pointer',
            }}
            title="Open Demo Control Center"
          >
            Controls
          </button>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={switching}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6,
              padding: '4px 12px',
              color: '#fff',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {switching ? 'Switching...' : `Viewing as: ${currentLabel}`}
            <span style={{ fontSize: 10 }}>{dropdownOpen ? '\u25B2' : '\u25BC'}</span>
          </button>

          {dropdownOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              background: '#1a1a2e',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8,
              minWidth: 220,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              overflow: 'hidden',
            }}>
              <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                Switch persona to explore different roles
              </div>
              <PersonaOption
                label="You (Admin)"
                sublabel="Full admin access"
                active={activePersona === 'owner'}
                onClick={() => switchPersona('owner')}
              />
              {personas.map(p => (
                <PersonaOption
                  key={p.key}
                  label={p.name}
                  sublabel={`${p.role}${p.credential_level ? ` / ${p.credential_level}` : ''}`}
                  active={activePersona === p.key}
                  onClick={() => switchPersona(p.key)}
                />
              ))}
            </div>
          )}
        </div>
        </div>
      ) : (
        <button
          onClick={() => switchPersona('owner')}
          disabled={switching}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6,
            padding: '4px 12px',
            color: '#fff',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {switching ? 'Switching...' : 'Exit Demo'}
        </button>
      )}
    </div>
  );
}

function PersonaOption({ label, sublabel, active, onClick }: {
  label: string;
  sublabel: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        background: active ? 'rgba(224,64,64,0.15)' : 'transparent',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        color: '#fff',
        cursor: active ? 'default' : 'pointer',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: active ? 600 : 400 }}>
        {active && '\u2713 '}{label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sublabel}</div>
    </button>
  );
}
